import React, { Component } from 'react';

import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import PropTypes from 'prop-types';

import { customFetchV2 } from '../../../services/custom-fetch-v2.ts';
import { Link, useHistory, useLocation } from 'react-router-dom';
import openExternalPage from '../../../services/openExternalPage';

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const usePaymentAlertStyles = makeStyles({
  action: { alignItems: 'flex-start', paddingTop: 4 },
});

function NoActiveDevicesAlert(props) {
  const { open, onClose } = props;

  return (
    <Snackbar open={open} style={{ zIndex: 1300, bottom: 'calc(var(--app-safe-area-inset-bottom) + 8px)' }}>
      <Alert onClose={onClose} severity="error">
        No active devices associated with your account.
        Try adding a new device from the menu on the left.
      </Alert>
    </Snackbar>
  );
}

function MissingLocationDataAlert(props) {
  const { open, onClose } = props;

  return (
    <Snackbar open={open} style={{ zIndex: 1300, bottom: 'calc(var(--app-safe-area-inset-bottom) + 8px)' }}>
      <Alert onClose={onClose} severity="warning">
        One or more of your trackers haven&apos;t yet uploaded their location data.
        Please ensure they&apos;re powered on.
      </Alert>
  </Snackbar>
  );
}

/**
 * When we're having trouble charging the user for an invoice. 
 * 
 * @param {*} props 
 * @returns 
 */
function InRecoveryAlert(props) {
  const { open } = props;

  return (
    <Snackbar open={open} style={{ zIndex: 1300, bottom: 'calc(var(--app-safe-area-inset-bottom) + 32px)' }}>
      <Alert severity="error">
        We&apos;re having trouble charging you for your subscription(s).{' '}
        <Link key="billing-link" style={{ color: 'white', textDecoration: 'underline' }} to={{
          pathname: '/m/billing',
          state: { origin: 'dashboard' },
        }}>Update your billing details</Link>{' '}
        to ensure uninterrupted service.
      </Alert>
    </Snackbar>
  );
}

/**
 * When the user needs to migrate their Stripe account from US to UK.
 * Shows a "processing" state if the user has just returned from Stripe Checkout,
 * since the webhook may not have fired yet.
 */
function StripeMigrationAlert(props) {
  const { open } = props;
  const classes = usePaymentAlertStyles();
  const history = useHistory();
  const location = useLocation();

  // Detect if the user just returned from Stripe Checkout. Held in state so it survives
  // re-renders from polling, but resets on a full page refresh (at which point the user
  // should see the action banner again in case they abandoned checkout).
  const [completedCheckout, setCompletedCheckout] = React.useState(
    () => new URLSearchParams(location.search).get('migration') === 'success',
  );
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const onDismiss = () => setDismissed(true);

  // A native deep link updates the router without remounting this banner.
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('migration');
    if (outcome !== 'success' && outcome !== 'cancel') return;

    setCompletedCheckout(outcome === 'success');
    setIsRedirecting(false);
    setDismissed(false);
    params.delete('migration');
    history.replace({ ...location, search: params.toString() ? `?${params.toString()}` : '' });
  }, [history, location]);

  // Checkout completion is provisional until the webhook updates the account.
  React.useEffect(() => {
    if (completedCheckout) {
      const timer = setTimeout(() => setDismissed(true), 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [completedCheckout]);

  const handleClick = async () => {
    setIsRedirecting(true);
    try {
      const platform = window.cordova || process.env.PLATFORM === 'mobile' ? 'mobile' : 'web';
      const response = await customFetchV2(`/api/billing/migration-checkout-sessions?platform=${platform}`, {
        method: 'POST',
      });
      if (response.ok && response.data.redirectUrl) {
        openExternalPage(response.data.redirectUrl);
      }
    } catch (e) {
      console.error('Failed to create migration checkout session', e);
    } finally {
      // Also allow retry when the user returns via Android Back rather than a callback.
      setIsRedirecting(false);
    }
  };

  // Dismissal lasts only for this mounted view; never save it as an account preference.
  if (dismissed) return null;

  // User just returned from Stripe Checkout — show a temporary info banner then hide entirely.
  // Don't fall through to the action banner since the migration is likely still processing.
  if (completedCheckout) {
    return (
      <Snackbar open={open} style={{ zIndex: 1300, bottom: 'calc(var(--app-safe-area-inset-bottom) + 32px)' }}>
        <Alert severity="info" classes={classes} onClose={onDismiss} closeText="Dismiss payment update alert">
          Your payment details are being updated. This may take a moment.
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Snackbar open={open} style={{ zIndex: 1300, bottom: 'calc(var(--app-safe-area-inset-bottom) + 32px)' }}>
      <Alert severity="error" classes={classes} onClose={onDismiss} closeText="Dismiss payment update alert">
        Please update your payment details to avoid interruption to service.{' '}
        <button
          type="button"
          onClick={handleClick}
          disabled={isRedirecting}
          style={{
            color: 'white',
            textDecoration: 'underline',
            cursor: isRedirecting ? 'default' : 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
          }}
        >
          {isRedirecting ? 'Redirecting...' : 'Update now'}
        </button>
      </Alert>
    </Snackbar>
  );
}

/**
 * Eventually we'll have this component query a backend API requesting alerts that should
 * be displayed. For now though, its a bit of a hack.
 */
class UserAlertsPanel extends Component {
  state = {
    hideNoActiveDevicesAlert: false,
    hideMissingLocationDataAlert: false,
  };

  componentDidMount() {
    const { config: { hideNoActiveDevicesAlert, hideMissingLocationDataAlert } } = this.props.user || {};

    this.setState({
      hideNoActiveDevicesAlert,
      hideMissingLocationDataAlert,
    });
  }

  showNoActiveDevicesAlert = () => {
    const { triggerNoActiveDevicesAlert } = this.props;
    const forceHide = this.state.hideNoActiveDevicesAlert;

    return triggerNoActiveDevicesAlert && !forceHide;
  }

  showMissingLocationDataAlert = () => {
    const { triggerMissingLocationDataAlert } = this.props;
    const forceHide = this.state.hideMissingLocationDataAlert;

    return triggerMissingLocationDataAlert && !forceHide;
  }

  showStripeMigrationAlert = () => {
    const { triggerStripeMigrationAlert } = this.props;
    return triggerStripeMigrationAlert;
  }

  showInRecoveryAlert = () => {
    const { triggerInRecoveryAlert } = this.props;
    return triggerInRecoveryAlert;
  }

  onCloseNoActiveDevicesAlert = () => {
    this.setState({ hideNoActiveDevicesAlert: true });
    this.setUserConfig('hideNoActiveDevicesAlert', true);
  }

  onCloseMissingLocationDataAlert = () => {
    this.setState({ hideMissingLocationDataAlert: true });

    // Intentionally disable permanent dismissle of the noLocationData alert
    // this.setUserConfig('hideMissingLocationDataAlert', true);
  }

  setUserConfig = (key, value) => {
    customFetchV2('/api/v2/users/me/config', {
      method: 'PATCH',
      body: JSON.stringify({
        [key]: value,
      }),
    });
  }


  render() {
    if (this.showStripeMigrationAlert()) {
      return (
        <StripeMigrationAlert
          open={this.showStripeMigrationAlert()}
        />
      );
    }

    if (this.showInRecoveryAlert()) {
      return (
        <InRecoveryAlert
          open={this.showInRecoveryAlert()}
        />
      );
    }
    
    return (
      <React.Fragment>
        <NoActiveDevicesAlert
          open={this.showNoActiveDevicesAlert()}
          onClose={this.onCloseNoActiveDevicesAlert}
        />

        <MissingLocationDataAlert
          open={this.showMissingLocationDataAlert()}
          onClose={this.onCloseMissingLocationDataAlert}
        />

      </React.Fragment>
    );
  }
}

UserAlertsPanel.propTypes = {
  user: PropTypes.object.isRequired,
  triggerNoActiveDevicesAlert: PropTypes.bool,
  triggerMissingLocationDataAlert: PropTypes.bool,
  triggerStripeMigrationAlert: PropTypes.bool,
};

export default UserAlertsPanel;
